// The scoreboard line's layout (projects/investing spec §5.5, D15: a
// hand-built SVG, no chart library) — Training's Progress chart math
// (training/lib/chart.js) on a time axis: snapshots can come several a day,
// so a point sits at its instant, not its calendar day. Pure: the figures
// come from lib/scoreboard.js in whole cents; they become floats here only
// to be placed in pixels, and every figure the page SAYS is formatted from
// the cents, never from these.

import { dayString, linePath, monthTicks, niceTicks, scaleLinear, weekTicks } from "../../training/lib/chart";

const DAY_MS = 86_400_000;
// The x axis spans at least this many days, so a day's readings don't
// stretch across the whole plot as if they were months.
const MIN_SPAN_DAYS = 14;
// Room either side of the first and last points, as a share of the span.
const LEAD_SHARE = 0.04;

const AXIS = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  notation: "compact",
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

/** A y-axis label: "$10.2K", "$950" — short enough for the axis. */
export function axisDollars(value) {
  return AXIS.format(value);
}

/**
 * Lay out the series (lib/scoreboard's points, oldest first) in a `width`
 * × `height` box with `margin` around the plot: the plot box, the y ticks
 * (`{value, y}`, dollars — one axis for both lines), the x ticks (`{label,
 * x}`: months, or weeks for a short history), each line's path, and the
 * points (`{x, yAccount, yShadow, point}`). Fewer than two points draw no
 * line, only dots.
 */
export function scoreboardChart(series, { width, height, margin }) {
  const plot = {
    left: margin.left,
    right: width - margin.right,
    top: margin.top,
    bottom: height - margin.bottom,
  };
  const points = (series ?? [])
    .map((point) => ({
      day: Date.parse(point.asOf) / DAY_MS,
      account: Number(point.account) / 100,
      shadow: Number(point.shadow) / 100,
      point,
    }))
    .filter((point) => Number.isFinite(point.day));
  if (points.length === 0) {
    return { plot, yTicks: [], xTicks: [], accountPath: "", shadowPath: "", points: [] };
  }
  const first = Math.min(...points.map((p) => p.day));
  const last = Math.max(...points.map((p) => p.day));
  const start = Math.min(first, last - MIN_SPAN_DAYS);
  const lead = (last - start) * LEAD_SHARE;
  const [from, to] = [start - lead, last + lead];
  const x = scaleLinear([from, to], [plot.left, plot.right]);
  const values = points.flatMap((p) => [p.account, p.shadow]);
  const axis = niceTicks(Math.min(...values), Math.max(...values));
  const y = scaleLinear([axis.min, axis.max], [plot.bottom, plot.top]);
  const placed = points.map((p) => ({ x: x(p.day), yAccount: y(p.account), yShadow: y(p.shadow), point: p.point }));
  const [firstDay, lastDay] = [dayString(Math.ceil(from)), dayString(Math.floor(to))];
  const months = monthTicks(firstDay, lastDay);
  return {
    plot,
    yTicks: axis.ticks.map((value) => ({ value, y: y(value) })),
    xTicks: (months.length >= 2 ? months : weekTicks(firstDay, lastDay)).map((tick) => ({ ...tick, x: x(tick.value) })),
    accountPath: linePath(placed.map((p) => ({ x: p.x, y: p.yAccount }))),
    shadowPath: linePath(placed.map((p) => ({ x: p.x, y: p.yShadow }))),
    points: placed,
  };
}
